import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import LoaderSpin from '../atoms/LoaderSpin';
import { ROUTE_CONFIG, RouteMenuItem } from '../../constants/routes';
import { hasAnyPermission, hasAllPermissions } from '../../lib/permissions';
import { ADMIN_ROLES } from '../../constants/system';

interface ProtectedRouteProps {
  requiredPermissions?: string[];
  requiredRoles?: string[];
  requireAllPermissions?: boolean;
  fallbackPath?: string;
  isLoading?: boolean;
}

const findFirstAllowedRoute = (
  userPermissions: string[],
  userRole: string | undefined,
  routes: RouteMenuItem[]
): string => {
  const isSuperAdmin = userRole === ADMIN_ROLES.superAdmin;

  const checkRouteAccess = (route: RouteMenuItem): boolean => {
    // If route has no auth config, it's accessible
    if (!route.auth) {
      return true;
    }

    const { requiredRoles, requiredPermissions, requireAllPermissions } =
      route.auth;

    // Check role requirement
    if (requiredRoles && requiredRoles.length > 0) {
      if (!isSuperAdmin && (!userRole || !requiredRoles.includes(userRole))) {
        return false;
      }
    }

    // Check permission requirement
    if (requiredPermissions && requiredPermissions.length > 0) {
      if (isSuperAdmin) {
        return true;
      }

      if (requireAllPermissions) {
        if (!hasAllPermissions(userPermissions, requiredPermissions)) {
          return false;
        }
      } else {
        if (!hasAnyPermission(userPermissions, requiredPermissions)) {
          return false;
        }
      }
    }

    return true;
  };

  // Flatten routes including children
  const flattenRoutes = (routes: RouteMenuItem[]): RouteMenuItem[] => {
    const result: RouteMenuItem[] = [];
    for (const route of routes) {
      // Only include routes with valid paths (starting with '/')
      if (route.key.startsWith('/')) {
        result.push(route);
      }
      if (route.children) {
        result.push(...flattenRoutes(route.children));
      }
    }
    return result;
  };

  const flatRoutes = flattenRoutes(routes);

  // Find first allowed route
  for (const route of flatRoutes) {
    if (checkRouteAccess(route)) {
      return route.key;
    }
  }

  // Default fallback
  return '/';
};

export function ProtectedRoute({
  requiredPermissions = [],
  requiredRoles = [],
  requireAllPermissions = false,
  fallbackPath,
  isLoading = false,
}: ProtectedRouteProps) {
  const { checkAnyPermission, checkAllPermissions, user, permissions } =
    usePermissions();
  const location = useLocation();

  const actualFallbackPath =
    fallbackPath ||
    findFirstAllowedRoute(permissions, user?.role?.code, ROUTE_CONFIG);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoaderSpin size="large" />
      </div>
    );
  }

  if (requiredPermissions.length === 0 && requiredRoles.length === 0) {
    return <Outlet />;
  }

  if (requiredRoles.length > 0) {
    const hasRequiredRole = user?.role?.code
      ? requiredRoles.includes(user.role.code)
      : false;

    if (!hasRequiredRole) {
      return (
        <Navigate to={actualFallbackPath} state={{ from: location }} replace />
      );
    }
  }

  if (requiredPermissions.length > 0) {
    let hasPermission = false;

    if (requireAllPermissions) {
      hasPermission = checkAllPermissions(requiredPermissions);
    } else {
      hasPermission = checkAnyPermission(requiredPermissions);
    }

    if (!hasPermission) {
      return (
        <Navigate to={actualFallbackPath} state={{ from: location }} replace />
      );
    }
  }

  return <Outlet />;
}
